import type {
  Card,
  Domain,
  Side,
  GameState,
  PlayerState,
  UnitInstance,
  GearInstance,
  RuneInstance,
  BattlefieldState,
  LogEntry,
  ParsedKeyword,
  SpellEffect,
} from './types';
import {
  VICTORY_SCORE,
  INITIAL_HAND_SIZE,
  CHANNEL_PER_TURN,
  CHANNEL_FIRST_TURN_P2,
  RUNE_DECK_SIZE,
} from './config';

// ─── Utilities ───────────────────────────────────────────
let _counter = 0;
export const uid = () => `${Date.now()}-${(_counter++).toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export const toNum = (v?: string | null) => {
  const n = parseInt(v ?? '0', 10);
  return Number.isFinite(n) ? n : 0;
};

export const shuffle = <T,>(arr: T[]): T[] => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export const sanitizeText = (desc?: string | null) =>
  (desc ?? '')
    .replace(/<br\s*\/?>(\r\n)?/gi, ' ')
    .replace(/<\/?em>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// ─── Card helpers ────────────────────────────────────────
export const getEnergyCost = (card: Card) => toNum(card.energyCost);
export const getPowerCost = (card: Card) => toNum(card.powerCost);
export const getMight = (card: Card) => toNum(card.might);

export const isUnit = (card: Card) => {
  const t = (card.cardType ?? '').toLowerCase();
  return t.includes('unit') && !t.includes('signature');
};
export const isChampionUnit = (card: Card) => (card.cardType ?? '').toLowerCase().includes('champion unit');
export const isSpellCard = (card: Card) => {
  const t = (card.cardType ?? '').toLowerCase();
  return t === 'spell' || t === 'signature spell';
};
export const isGearCard = (card: Card) => (card.cardType ?? '').toLowerCase() === 'gear';
export const isLegend = (card: Card) => (card.cardType ?? '').toLowerCase() === 'legend';
export const isBattlefieldCard = (card: Card) => (card.cardType ?? '').toLowerCase() === 'battlefield';
export const isRuneCard = (card: Card) => (card.cardType ?? '').toLowerCase() === 'rune';
export const isSignatureCard = (card: Card) => (card.cardType ?? '').toLowerCase().includes('signature');
export const isTokenCard = (card: Card) => (card.cardType ?? '').toLowerCase() === 'token';

export const isPlayableCard = (card: Card) =>
  isUnit(card) || isSpellCard(card) || isGearCard(card) || isChampionUnit(card) || isSignatureCard(card);

export const getCardDomains = (card: Card): Domain[] => {
  if (!card.domain) return [];
  return card.domain.split(';').filter(Boolean) as Domain[];
};

// ─── Keyword parsing ────────────────────────────────────
export function parseKeywords(card: Card): ParsedKeyword[] {
  const text = sanitizeText(card.description);
  const keywords: ParsedKeyword[] = [];

  const patterns: [RegExp, import('./types').Keyword][] = [
    [/SHIELD\s*(\d*)/i, 'SHIELD'],
    [/ASSAULT\s*(\d*)/i, 'ASSAULT'],
    [/TANK/i, 'TANK'],
    [/DEFLECT\s*(\d*)/i, 'DEFLECT'],
    [/GANKING/i, 'GANKING'],
    [/ACCELERATE/i, 'ACCELERATE'],
    [/HIDDEN/i, 'HIDDEN'],
    [/DEATHKNELL/i, 'DEATHKNELL'],
    [/TEMPORARY/i, 'TEMPORARY'],
    [/VISION/i, 'VISION'],
    [/LEGION/i, 'LEGION'],
  ];

  for (const [pattern, keyword] of patterns) {
    const match = text.match(pattern);
    if (match) {
      const value = match[1] ? parseInt(match[1], 10) : 1;
      keywords.push({ keyword, value: Number.isFinite(value) ? value : 1 });
    }
  }

  return keywords;
}

// ─── Spell effect parsing ────────────────────────────────
export function parseSpellEffects(card: Card): SpellEffect[] {
  const text = sanitizeText(card.description);
  const effects: SpellEffect[] = [];

  // Deal X to all enemy units
  const dealAll = text.match(/deal\s+(\d+)\s+(?:damage\s+)?to\s+all\s+(?:enemy\s+)?units/i);
  if (dealAll) effects.push({ kind: 'damageAll', amount: parseInt(dealAll[1], 10), target: 'enemy' });

  // Deal X to a unit (single target)
  const dealOne = text.match(/deal\s+(\d+)\s+(?:damage\s+)?to\s+(?:a|an)\s+unit/i);
  if (dealOne) effects.push({ kind: 'damage', amount: parseInt(dealOne[1], 10), target: 'enemy' });

  // Kill a unit
  if (/kill\s+(?:a|an)\s+unit/i.test(text)) effects.push({ kind: 'kill', amount: 1, target: 'enemy' });

  // Kill all gear
  if (/kill\s+all\s+gear/i.test(text)) effects.push({ kind: 'killGear', amount: 0 });

  // Stun a unit
  if (/stun\s+(?:a|an)\s+(?:enemy\s+)?unit/i.test(text) || /stun\s+(?:a|an)\s+enemy/i.test(text))
    effects.push({ kind: 'stun', amount: 1, target: 'enemy' });

  // Give friendly units +X Might
  const buffAll = text.match(/give\s+friendly\s+units?\s+\+(\d+)\s+might/i);
  if (buffAll) effects.push({ kind: 'mightBonusAll', amount: parseInt(buffAll[1], 10), target: 'friendly' });

  // Give a unit +X Might / +X Might
  const buffOne = text.match(/give\s+(?:a|an)\s+(?:friendly\s+)?unit\s+\+(\d+)\s+might/i);
  if (buffOne && !buffAll) effects.push({ kind: 'mightBonus', amount: parseInt(buffOne[1], 10), target: 'friendly' });

  // Give a unit ASSAULT X
  const assaultBuff = text.match(/give\s+(?:a|an)\s+unit\s+ASSAULT\s+(\d+)/i);
  if (assaultBuff) effects.push({ kind: 'mightBonus', amount: parseInt(assaultBuff[1], 10), target: 'friendly' });

  // Buff a friendly unit
  if (/buff\s+(?:a|an)\s+friendly\s+unit/i.test(text)) effects.push({ kind: 'buff', amount: 1, target: 'friendly' });

  // Draw X
  const drawMatch = text.match(/draw\s+(\d+)/i);
  if (drawMatch) effects.push({ kind: 'draw', amount: parseInt(drawMatch[1], 10) });

  // Deal X to a unit (specific damage amounts at end)
  if (effects.length === 0) {
    const genericDeal = text.match(/deal\s+(\d+)/i);
    if (genericDeal) effects.push({ kind: 'damage', amount: parseInt(genericDeal[1], 10), target: 'enemy' });
  }

  // Do this N times: Deal X
  const multiDeal = text.match(/do\s+this\s+(\d+)\s+times.*?deal\s+(\d+)/i);
  if (multiDeal) {
    effects.length = 0; // clear previous
    effects.push({ kind: 'damageMulti', amount: parseInt(multiDeal[2], 10) * parseInt(multiDeal[1], 10), target: 'enemy' });
  }

  // Summon tokens
  const tokens = text.match(/play\s+(?:four|three|two|(\d+))\s+(\d+)\s+might/i);
  if (tokens) {
    const count = tokens[1] ? parseInt(tokens[1], 10) : (text.includes('four') ? 4 : text.includes('three') ? 3 : 2);
    effects.push({ kind: 'summonTokens', amount: count });
  }

  return effects;
}

// ─── Create unit instance ────────────────────────────────
export function createUnit(card: Card, location: UnitInstance['location'] = 'base'): UnitInstance {
  const might = Math.max(1, getMight(card));
  return {
    instanceId: uid(),
    card,
    baseMight: might,
    currentMight: might,
    damageTaken: 0,
    exhausted: true, // enters exhausted by default
    keywords: parseKeywords(card),
    tempMightBonus: 0,
    buffCount: 0,
    stunned: false,
    temporary: false,
    location,
  };
}

// ─── Effective might calculation ─────────────────────────
export function getEffectiveMight(
  unit: UnitInstance,
  context: 'attacking' | 'defending' | 'idle' = 'idle'
): number {
  let might = unit.baseMight + unit.buffCount + unit.tempMightBonus;
  for (const kw of unit.keywords) {
    if (kw.keyword === 'ASSAULT' && context === 'attacking') might += kw.value;
    if (kw.keyword === 'SHIELD' && context === 'defending') might += kw.value;
  }
  return Math.max(1, might);
}

// ─── Health (damage needed to kill) ─────────────────────
// SHIELD / ASSAULT modify combat Might only, not how much damage a unit can take.
export function getUnitHealth(unit: UnitInstance): number {
  return Math.max(1, unit.baseMight + unit.buffCount + unit.tempMightBonus);
}

// ─── Is unit alive? ─────────────────────────────────────
export function isAlive(unit: UnitInstance): boolean {
  return unit.damageTaken < getUnitHealth(unit);
}

// ─── Create rune instances ───────────────────────────────
export function createRuneDeck(domains: Domain[]): Card[] {
  const runes: Card[] = [];
  const half = Math.floor(RUNE_DECK_SIZE / 2);
  const d1 = domains[0] || 'Fury';
  const d2 = domains[1] || domains[0] || 'Fury';

  for (let i = 0; i < half; i++) {
    runes.push({
      id: `rune-${d1}-${i}`,
      name: `${d1} Rune`,
      cardType: 'Rune',
      domain: d1,
    });
  }
  for (let i = 0; i < RUNE_DECK_SIZE - half; i++) {
    runes.push({
      id: `rune-${d2}-${i}`,
      name: `${d2} Rune`,
      cardType: 'Rune',
      domain: d2,
    });
  }
  return shuffle(runes);
}

export function runeFromCard(card: Card): RuneInstance {
  return {
    instanceId: uid(),
    card,
    domain: (card.domain as Domain) || 'Fury',
    exhausted: false,
  };
}

// ─── Get available energy ────────────────────────────────
export function getAvailableEnergy(player: PlayerState): number {
  return player.runesInPlay.filter((r) => !r.exhausted).length;
}

// ─── Get available power of a specific domain ────────────
export function getAvailablePower(player: PlayerState, domain: Domain): number {
  return player.runesInPlay.filter((r) => !r.exhausted && r.domain === domain).length;
}

// ─── Can afford a card? ──────────────────────────────────
export function canAfford(player: PlayerState, card: Card): boolean {
  const energy = getEnergyCost(card);
  const power = getPowerCost(card);
  const available = getAvailableEnergy(player);
  if (available < energy + power) return false;

  // Check domain-specific power cost
  if (power > 0) {
    const domains = getCardDomains(card);
    if (domains.length > 0) {
      // Need at least 'power' runes of the right domain(s) to recycle
      const domainRunes = player.runesInPlay.filter(
        (r) => !r.exhausted && domains.includes(r.domain)
      ).length;
      if (domainRunes < power) return false;
    }
  }
  return true;
}

// ─── Pay costs ───────────────────────────────────────────
export function payCosts(player: PlayerState, card: Card): void {
  const energy = getEnergyCost(card);
  const power = getPowerCost(card);
  const domains = getCardDomains(card);

  // Pay power cost first (recycle domain runes)
  let powerPaid = 0;
  if (power > 0) {
    for (let i = player.runesInPlay.length - 1; i >= 0 && powerPaid < power; i--) {
      const rune = player.runesInPlay[i];
      if (!rune.exhausted && domains.includes(rune.domain)) {
        player.runesInPlay.splice(i, 1);
        player.runeDeck.push(rune.card); // recycle to bottom
        powerPaid++;
      }
    }
  }

  // Pay energy cost (exhaust runes)
  let energyPaid = 0;
  for (const rune of player.runesInPlay) {
    if (energyPaid >= energy) break;
    if (!rune.exhausted) {
      rune.exhausted = true;
      energyPaid++;
    }
  }
}

// ─── Channel runes ───────────────────────────────────────
export function channelRunes(player: PlayerState, count: number, logs: LogEntry[], turn: number): void {
  const channeled = Math.min(count, player.runeDeck.length);
  for (let i = 0; i < channeled; i++) {
    const card = player.runeDeck.shift();
    if (card) {
      player.runesInPlay.push(runeFromCard(card));
    }
  }
  if (channeled > 0) {
    logs.push({
      turn,
      side: player.side,
      message: `⚡ ${player.name} canalise ${channeled} rune${channeled > 1 ? 's' : ''}`,
      type: 'info',
    });
  }
}

// ─── Draw cards ──────────────────────────────────────────
export function drawCards(player: PlayerState, count: number, logs: LogEntry[], turn: number): void {
  const drawn = Math.min(count, player.mainDeck.length);
  for (let i = 0; i < drawn; i++) {
    const card = player.mainDeck.shift();
    if (card) player.hand.push(card);
  }
  if (drawn > 0) {
    logs.push({
      turn,
      side: player.side,
      message: `📥 ${player.name} pioche ${drawn} carte${drawn > 1 ? 's' : ''}`,
      type: 'draw',
    });
  }
}

// ─── Awaken (ready all) ──────────────────────────────────
export function awakenPhase(player: PlayerState): void {
  for (const rune of player.runesInPlay) rune.exhausted = false;
  for (const unit of player.units) unit.exhausted = false;
  for (const gear of player.gear) gear.exhausted = false;
}

// ─── Beginning phase ─────────────────────────────────────
export function beginningPhase(player: PlayerState, state: GameState, logs: LogEntry[]): void {
  // Kill TEMPORARY units
  const toRemove: string[] = [];
  for (const unit of player.units) {
    if (unit.temporary) {
      toRemove.push(unit.instanceId);
      player.trash.push(unit.card);
      logs.push({
        turn: state.turnNumber,
        side: player.side,
        message: `💀 ${unit.card.name} (TEMPORARY) est détruit`,
        type: 'death',
      });
    }
  }
  player.units = player.units.filter((u) => !toRemove.includes(u.instanceId));

  // Clear temp might bonuses
  for (const unit of player.units) {
    unit.tempMightBonus = 0;
    unit.stunned = false;
  }

  // Score points for held battlefields (+ "When you hold here" effects)
  for (const bf of state.battlefields) {
    if (bf.controlledBy === player.side && bf.conqueredTurn < state.turnNumber) {
      player.score++;
      logs.push({
        turn: state.turnNumber,
        side: player.side,
        message: `🏆 ${player.name} gagne 1 point en tenant ${bf.card.name} (${player.score} pts)`,
        type: 'score',
      });
      applyBattlefieldEffect(state, player.side, bf, 'hold', logs);
    }
  }
}

// ─── Resolve deaths ──────────────────────────────────────
export function resolveDeaths(state: GameState, logs: LogEntry[]): void {
  for (const side of ['player', 'opponent'] as Side[]) {
    const p = state[side];
    const dead: UnitInstance[] = [];
    const alive: UnitInstance[] = [];

    for (const unit of p.units) {
      const totalHP = getUnitHealth(unit);
      if (unit.damageTaken >= totalHP) {
        dead.push(unit);
        p.trash.push(unit.card);
        logs.push({
          turn: state.turnNumber,
          side,
          message: `💀 ${unit.card.name} est détruit (${unit.damageTaken}/${totalHP} dégâts)`,
          type: 'death',
        });
      } else {
        alive.push(unit);
      }
    }
    p.units = alive;
  }
}

// Strongest enemy unit, preferring those on a battlefield
function pickEnemyTarget(units: UnitInstance[]): UnitInstance | undefined {
  const byMight = (a: UnitInstance, b: UnitInstance) => getEffectiveMight(b) - getEffectiveMight(a);
  const onBf = units.filter((u) => u.location !== 'base').sort(byMight);
  return onBf[0] ?? [...units].sort(byMight)[0];
}

// ─── Apply spell effects ─────────────────────────────────
export function applySpellEffects(
  state: GameState,
  side: Side,
  card: Card,
  logs: LogEntry[],
  targetUnitId?: string
): void {
  const effects = parseSpellEffects(card);
  const actor = state[side];
  const opponent = state[side === 'player' ? 'opponent' : 'player'];

  if (effects.length === 0) {
    // Check for basic keyword-based effects from description
    const kws = parseKeywords(card);
    if (kws.length > 0) {
      logs.push({
        turn: state.turnNumber,
        side,
        message: `✨ ${actor.name} joue ${card.name} (${kws.map((k) => k.keyword).join(', ')})`,
        type: 'spell',
      });
    } else {
      logs.push({
        turn: state.turnNumber,
        side,
        message: `✨ ${actor.name} joue ${card.name}`,
        type: 'spell',
      });
    }
    return;
  }

  for (const effect of effects) {
    switch (effect.kind) {
      case 'damageAll': {
        const targets = opponent.units.filter((u) => u.location !== 'base');
        for (const t of targets) t.damageTaken += effect.amount;
        logs.push({
          turn: state.turnNumber,
          side,
          message: `🔥 ${card.name}: ${effect.amount} dégâts à toutes les unités ennemies (${targets.length} cibles)`,
          type: 'spell',
        });
        break;
      }

      case 'damage': {
        let target: UnitInstance | undefined;
        if (targetUnitId) {
          target = opponent.units.find((u) => u.instanceId === targetUnitId);
        }
        if (!target) target = pickEnemyTarget(opponent.units);
        if (target) {
          target.damageTaken += effect.amount;
          logs.push({
            turn: state.turnNumber,
            side,
            message: `🎯 ${card.name}: ${effect.amount} dégâts sur ${target.card.name}`,
            type: 'spell',
          });
        } else {
          logs.push({
            turn: state.turnNumber,
            side,
            message: `🎯 ${card.name}: pas de cible`,
            type: 'spell',
          });
        }
        break;
      }

      case 'damageMulti': {
        const bfUnits = opponent.units.filter((u) => u.location !== 'base');
        if (bfUnits.length > 0) {
          // Split damage among units
          const dmgPer = Math.ceil(effect.amount / bfUnits.length);
          for (const u of bfUnits) u.damageTaken += dmgPer;
          logs.push({
            turn: state.turnNumber,
            side,
            message: `🔥 ${card.name}: ${effect.amount} dégâts répartis (${dmgPer} chacun)`,
            type: 'spell',
          });
        }
        break;
      }

      case 'kill': {
        let target: UnitInstance | undefined;
        if (targetUnitId) {
          target = opponent.units.find((u) => u.instanceId === targetUnitId);
        }
        if (!target) target = pickEnemyTarget(opponent.units);
        if (target) {
          target.damageTaken = 9999; // lethal
          logs.push({
            turn: state.turnNumber,
            side,
            message: `💀 ${card.name}: ${target.card.name} est détruit`,
            type: 'spell',
          });
        }
        break;
      }

      case 'stun': {
        let target: UnitInstance | undefined;
        if (targetUnitId) {
          target = opponent.units.find((u) => u.instanceId === targetUnitId);
        }
        if (!target) target = pickEnemyTarget(opponent.units);
        if (target) {
          target.stunned = true;
          logs.push({
            turn: state.turnNumber,
            side,
            message: `⛓️ ${card.name}: ${target.card.name} est étourdi`,
            type: 'spell',
          });
        }
        break;
      }

      case 'mightBonus': {
        const allyUnits = actor.units.filter((u) => u.location !== 'base');
        const target = allyUnits.sort((a, b) => getEffectiveMight(b) - getEffectiveMight(a))[0];
        if (target) {
          target.tempMightBonus += effect.amount;
          logs.push({
            turn: state.turnNumber,
            side,
            message: `📈 ${card.name}: ${target.card.name} gagne +${effect.amount} Might`,
            type: 'spell',
          });
        }
        break;
      }

      case 'mightBonusAll': {
        const allyUnits = actor.units;
        for (const u of allyUnits) u.tempMightBonus += effect.amount;
        logs.push({
          turn: state.turnNumber,
          side,
          message: `📈 ${card.name}: toutes les unités alliées gagnent +${effect.amount} Might`,
          type: 'spell',
        });
        break;
      }

      case 'buff': {
        const allyUnits = actor.units;
        const target = allyUnits.sort((a, b) => getEffectiveMight(b) - getEffectiveMight(a))[0];
        if (target) {
          target.buffCount++;
          logs.push({
            turn: state.turnNumber,
            side,
            message: `⬆️ ${card.name}: ${target.card.name} reçoit un buff permanent`,
            type: 'spell',
          });
        }
        break;
      }

      case 'draw': {
        drawCards(actor, effect.amount, logs, state.turnNumber);
        break;
      }

      case 'killGear': {
        const enemyGear = opponent.gear.length;
        for (const g of opponent.gear) opponent.trash.push(g.card);
        opponent.gear = [];
        logs.push({
          turn: state.turnNumber,
          side,
          message: `🗑️ ${card.name}: ${enemyGear} gear(s) détruit(s)`,
          type: 'spell',
        });
        break;
      }

      case 'summonTokens': {
        for (let i = 0; i < effect.amount; i++) {
          const token = createUnit(
            {
              id: `token-${uid()}`,
              name: 'Recruit',
              cardType: 'Token',
              domain: actor.legend?.domain || 'Order',
              might: '1',
              energyCost: '0',
            },
            'base'
          );
          token.exhausted = false;
          actor.units.push(token);
        }
        logs.push({
          turn: state.turnNumber,
          side,
          message: `🛡️ ${card.name}: ${effect.amount} Recruit(s) invoqué(s)`,
          type: 'spell',
        });
        break;
      }

      case 'mightMinus': {
        const bfUnits = opponent.units.filter((u) => u.location !== 'base');
        const target = bfUnits.sort((a, b) => getEffectiveMight(b) - getEffectiveMight(a))[0];
        if (target) {
          target.tempMightBonus -= effect.amount;
          logs.push({
            turn: state.turnNumber,
            side,
            message: `📉 ${card.name}: ${target.card.name} perd ${effect.amount} Might`,
            type: 'spell',
          });
        }
        break;
      }
    }
  }

  resolveDeaths(state, logs);
}

// ─── Play a card from hand ───────────────────────────────
export function playCard(
  state: GameState,
  side: Side,
  handIndex: number,
  logs: LogEntry[],
  targetUnitId?: string
): boolean {
  const player = state[side];
  if (handIndex < 0 || handIndex >= player.hand.length) return false;

  const card = player.hand[handIndex];
  if (!canAfford(player, card)) return false;

  // Pay costs
  payCosts(player, card);
  player.hand.splice(handIndex, 1);
  player.cardsPlayedThisTurn++;

  const type = (card.cardType ?? '').toLowerCase();

  if (type.includes('spell') || type === 'signature spell') {
    // Spell: resolve effects then trash
    logs.push({
      turn: state.turnNumber,
      side,
      message: `✨ ${player.name} lance le sort ${card.name}`,
      type: 'spell',
    });
    applySpellEffects(state, side, card, logs, targetUnitId);
    player.trash.push(card);
  } else if (type === 'gear') {
    // Gear: enters the board
    const gear: GearInstance = {
      instanceId: uid(),
      card,
      exhausted: false,
    };
    player.gear.push(gear);
    logs.push({
      turn: state.turnNumber,
      side,
      message: `⚙️ ${player.name} équipe ${card.name}`,
      type: 'info',
    });
  } else if (type.includes('unit')) {
    // Unit: enters base exhausted
    const unit = createUnit(card, 'base');

    // Check ACCELERATE
    const hasAccelerate = unit.keywords.some((k) => k.keyword === 'ACCELERATE');
    if (hasAccelerate) {
      unit.exhausted = false; // enters ready
      logs.push({
        turn: state.turnNumber,
        side,
        message: `⚡ ${card.name} entre prêt (ACCELERATE)`,
        type: 'keyword',
      });
    }

    // Check "enters ready"
    const desc = sanitizeText(card.description);
    if (/I enter ready/i.test(desc) || /enters? ready/i.test(desc)) {
      unit.exhausted = false;
    }

    // Apply on-play effects from description
    applyOnPlayEffects(state, side, unit, logs);

    player.units.push(unit);
    logs.push({
      turn: state.turnNumber,
      side,
      message: `🛡️ ${player.name} invoque ${card.name} (${getEffectiveMight(unit)} Might) en base`,
      type: 'info',
    });
  }

  return true;
}

// ─── On-play effects ─────────────────────────────────────
function applyOnPlayEffects(state: GameState, side: Side, unit: UnitInstance, logs: LogEntry[]): void {
  const text = sanitizeText(unit.card.description);
  const opponent = state[side === 'player' ? 'opponent' : 'player'];

  // "When you play me, deal X damage to all units"
  const dealAllMatch = text.match(/when you play me.*?deal\s+(\d+)\s+damage\s+to\s+all\s+units/i);
  if (dealAllMatch) {
    const dmg = parseInt(dealAllMatch[1], 10);
    for (const u of opponent.units) u.damageTaken += dmg;
    for (const u of state[side].units) {
      if (u.instanceId !== unit.instanceId) u.damageTaken += dmg;
    }
    logs.push({
      turn: state.turnNumber,
      side,
      message: `🔥 ${unit.card.name}: ${dmg} dégâts à toutes les unités`,
      type: 'spell',
    });
    resolveDeaths(state, logs);
  }

  // "When you play me, draw X"
  const drawMatch = text.match(/when you play me.*?draw\s+(\d+)/i);
  if (drawMatch) {
    drawCards(state[side], parseInt(drawMatch[1], 10), logs, state.turnNumber);
  }

  // "When you play me, return a spell from your trash"
  if (/when you play me.*?return\s+a\s+spell.*?trash.*?hand/i.test(text)) {
    const spellInTrash = state[side].trash.findIndex((c) => isSpellCard(c));
    if (spellInTrash >= 0) {
      const spell = state[side].trash.splice(spellInTrash, 1)[0];
      state[side].hand.push(spell);
      logs.push({
        turn: state.turnNumber,
        side,
        message: `♻️ ${unit.card.name}: ${spell.name} retourne en main`,
        type: 'info',
      });
    }
  }
}

// ─── Play champion ───────────────────────────────────────
export function playChampion(
  state: GameState,
  side: Side,
  logs: LogEntry[]
): boolean {
  const player = state[side];
  if (!player.championCard || !player.championAvailable) return false;
  if (!canAfford(player, player.championCard)) return false;

  payCosts(player, player.championCard);
  const unit = createUnit(player.championCard, 'base');
  player.units.push(unit);
  player.championAvailable = false;

  logs.push({
    turn: state.turnNumber,
    side,
    message: `👑 ${player.name} invoque son Champion ${player.championCard.name}!`,
    type: 'info',
  });

  return true;
}

// ─── Move unit ───────────────────────────────────────────
export function moveUnit(
  state: GameState,
  side: Side,
  unitInstanceId: string,
  destination: UnitInstance['location'],
  logs: LogEntry[]
): boolean {
  const player = state[side];
  const unit = player.units.find((u) => u.instanceId === unitInstanceId);
  if (!unit) return false;
  if (unit.exhausted) return false;
  if (unit.location === destination) return false;

  // Moving from base to battlefield or between battlefields
  if (destination !== 'base' && unit.location !== 'base') {
    // GANKING required to move between battlefields
    if (!unit.keywords.some((k) => k.keyword === 'GANKING')) return false;
  }

  unit.exhausted = true;
  const from = unit.location;
  unit.location = destination;

  const destName = destination === 'base' ? 'la base' : state.battlefields.find((b) => b.id === destination)?.card.name ?? destination;
  logs.push({
    turn: state.turnNumber,
    side,
    message: `➡️ ${player.name} déplace ${unit.card.name} vers ${destName}`,
    type: 'info',
  });

  return true;
}

// ─── Showdown (combat at a battlefield) ──────────────────
export function resolveShowdown(
  state: GameState,
  attackerSide: Side,
  battlefieldId: 'battlefield1' | 'battlefield2',
  logs: LogEntry[]
): void {
  const attacker = state[attackerSide];
  const defender = state[attackerSide === 'player' ? 'opponent' : 'player'];
  const bf = state.battlefields.find((b) => b.id === battlefieldId)!;

  const attackerUnits = attacker.units.filter((u) => u.location === battlefieldId && !u.stunned);
  const defenderUnits = defender.units.filter((u) => u.location === battlefieldId);

  if (attackerUnits.length === 0) {
    logs.push({
      turn: state.turnNumber,
      side: attackerSide,
      message: `⚔️ Pas d'attaquants à ${bf.card.name}`,
      type: 'combat',
    });
    return;
  }

  // No defenders => conquer (only if not already ours: holding scores at the Beginning phase)
  if (defenderUnits.length === 0) {
    if (bf.controlledBy === attackerSide) {
      logs.push({
        turn: state.turnNumber,
        side: attackerSide,
        message: `🏰 ${bf.card.name} est déjà sous votre contrôle`,
        type: 'combat',
      });
      return;
    }
    conquerBattlefield(state, attackerSide, bf, logs);
    return;
  }

  logs.push({
    turn: state.turnNumber,
    side: attackerSide,
    message: `⚔️ SHOWDOWN à ${bf.card.name}! ${attackerUnits.length} vs ${defenderUnits.length}`,
    type: 'combat',
  });

  // Calculate total might
  let attackerTotalMight = 0;
  for (const u of attackerUnits) {
    attackerTotalMight += u.stunned ? 0 : getEffectiveMight(u, 'attacking');
  }

  let defenderTotalMight = 0;
  for (const u of defenderUnits) {
    defenderTotalMight += u.stunned ? 0 : getEffectiveMight(u, 'defending');
  }

  logs.push({
    turn: state.turnNumber,
    side: 'system' as Side,
    message: `⚔️ Puissance: Attaquants ${attackerTotalMight} vs Défenseurs ${defenderTotalMight}`,
    type: 'combat',
  });

  // Distribute attacker damage to defenders (TANK first, then strongest)
  let remainingAttDmg = attackerTotalMight;
  const defSorted = [...defenderUnits].sort((a, b) => {
    const aHasTank = a.keywords.some((k) => k.keyword === 'TANK') ? 0 : 1;
    const bHasTank = b.keywords.some((k) => k.keyword === 'TANK') ? 0 : 1;
    if (aHasTank !== bHasTank) return aHasTank - bHasTank;
    return getEffectiveMight(b, 'defending') - getEffectiveMight(a, 'defending');
  });

  for (const def of defSorted) {
    if (remainingAttDmg <= 0) break;
    const defHP = Math.max(0, getUnitHealth(def) - def.damageTaken);
    const dmgToApply = Math.min(remainingAttDmg, defHP);
    if (dmgToApply <= 0) continue;
    def.damageTaken += dmgToApply;
    remainingAttDmg -= dmgToApply;
    logs.push({
      turn: state.turnNumber,
      side: attackerSide,
      message: `  ⚔️ → ${def.card.name} reçoit ${dmgToApply} dégâts`,
      type: 'combat',
    });
  }

  // Distribute defender damage to attackers
  let remainingDefDmg = defenderTotalMight;
  const attSorted = [...attackerUnits].sort((a, b) => {
    const aHasTank = a.keywords.some((k) => k.keyword === 'TANK') ? 0 : 1;
    const bHasTank = b.keywords.some((k) => k.keyword === 'TANK') ? 0 : 1;
    if (aHasTank !== bHasTank) return aHasTank - bHasTank;
    return getEffectiveMight(b, 'attacking') - getEffectiveMight(a, 'attacking');
  });

  for (const att of attSorted) {
    if (remainingDefDmg <= 0) break;
    const attHP = Math.max(0, getUnitHealth(att) - att.damageTaken);
    const dmgToApply = Math.min(remainingDefDmg, attHP);
    if (dmgToApply <= 0) continue;
    att.damageTaken += dmgToApply;
    remainingDefDmg -= dmgToApply;
    logs.push({
      turn: state.turnNumber,
      side: attackerSide === 'player' ? 'opponent' : 'player',
      message: `  ⚔️ → ${att.card.name} reçoit ${dmgToApply} dégâts`,
      type: 'combat',
    });
  }

  // Resolve deaths
  resolveDeaths(state, logs);

  // Check aftermath
  const survivingDefenders = defender.units.filter((u) => u.location === battlefieldId);
  const survivingAttackers = attacker.units.filter((u) => u.location === battlefieldId);

  if (survivingDefenders.length === 0 && survivingAttackers.length > 0) {
    // Attackers conquer
    if (bf.controlledBy !== attackerSide) conquerBattlefield(state, attackerSide, bf, logs);
  } else if (survivingAttackers.length > 0 && survivingDefenders.length > 0) {
    // Failed to conquer, attackers return to base
    for (const u of survivingAttackers) {
      u.location = 'base';
      u.exhausted = true;
    }
    logs.push({
      turn: state.turnNumber,
      side: attackerSide,
      message: `🔙 Les attaquants retournent en base`,
      type: 'combat',
    });
  }
}

// ─── Conquer a battlefield ───────────────────────────────
function conquerBattlefield(
  state: GameState,
  side: Side,
  bf: BattlefieldState,
  logs: LogEntry[]
): void {
  const player = state[side];
  bf.controlledBy = side;
  bf.conqueredTurn = state.turnNumber;
  player.score++;

  logs.push({
    turn: state.turnNumber,
    side,
    message: `🏰 ${player.name} conquiert ${bf.card.name}! (+1 point → ${player.score} pts)`,
    type: 'score',
  });

  // Apply battlefield conquer effect
  applyBattlefieldEffect(state, side, bf, 'conquer', logs);
}

// ─── Battlefield effects ─────────────────────────────────
function applyBattlefieldEffect(
  state: GameState,
  side: Side,
  bf: BattlefieldState,
  trigger: 'conquer' | 'hold',
  logs: LogEntry[]
): void {
  const text = sanitizeText(bf.card.description);
  const player = state[side];

  if (trigger === 'hold' && /when you hold/i.test(text)) {
    // Grove of the God-Willow: draw 1
    if (/when you hold.*?draw\s+(\d+)/i.test(text)) {
      const m = text.match(/draw\s+(\d+)/i);
      drawCards(player, m ? parseInt(m[1], 10) : 1, logs, state.turnNumber);
    }
    // Altar: summon recruit
    if (/recruit.*?token/i.test(text)) {
      const token = createUnit({
        id: `token-${uid()}`,
        name: 'Recruit',
        cardType: 'Token',
        might: '1',
        energyCost: '0',
      }, 'base');
      token.exhausted = false;
      player.units.push(token);
      logs.push({
        turn: state.turnNumber,
        side,
        message: `🛡️ ${bf.card.name}: un Recruit apparaît en base`,
        type: 'info',
      });
    }
    // Navori: buff a unit
    if (/buff a unit/i.test(text)) {
      const bfUnit = player.units.filter((u) => u.location === bf.id)[0];
      if (bfUnit) {
        bfUnit.buffCount++;
        logs.push({
          turn: state.turnNumber,
          side,
          message: `⬆️ ${bf.card.name}: ${bfUnit.card.name} est buffé`,
          type: 'info',
        });
      }
    }
    // Startipped Peak: channel 1 rune
    if (/channel\s+1\s+rune/i.test(text)) {
      channelRunes(player, 1, logs, state.turnNumber);
    }
  }

  if (trigger === 'conquer') {
    // Targon's Peak: ready 2 runes
    if (/ready\s+(\d+)\s+rune/i.test(text)) {
      const m = text.match(/ready\s+(\d+)/i);
      const count = m ? parseInt(m[1], 10) : 2;
      let readied = 0;
      for (const r of player.runesInPlay) {
        if (readied >= count) break;
        if (r.exhausted) {
          r.exhausted = false;
          readied++;
        }
      }
      if (readied > 0) {
        logs.push({
          turn: state.turnNumber,
          side,
          message: `⚡ ${bf.card.name}: ${readied} rune(s) prête(s)`,
          type: 'info',
        });
      }
    }
    // Zaun Warrens: discard 1, draw 1
    if (/discard.*?draw/i.test(text)) {
      if (player.hand.length > 0) {
        player.trash.push(player.hand.pop()!);
        drawCards(player, 1, logs, state.turnNumber);
      }
    }
    // Draw on conquer
    if (/when you conquer.*?draw\s+(\d+)/i.test(text)) {
      const m = text.match(/draw\s+(\d+)/i);
      drawCards(player, m ? parseInt(m[1], 10) : 1, logs, state.turnNumber);
    }
  }
}

// ─── Check winner ────────────────────────────────────────
export function checkWinner(state: GameState): Side | null {
  if (state.player.score >= state.victoryScore) return 'player';
  if (state.opponent.score >= state.victoryScore) return 'opponent';
  return null;
}

// ─── Initialize game ─────────────────────────────────────
export function initializeGame(
  allCards: Card[],
  playerLegend: Card | null,
  playerDeckCards: Card[],
  playerBattlefield: Card,
  botLegend: Card | null,
  botDeckCards: Card[],
  botBattlefield: Card,
  logs: LogEntry[]
): GameState {
  // Determine domains
  const playerDomains = playerLegend ? getCardDomains(playerLegend) : ['Fury' as Domain, 'Chaos' as Domain];
  const botDomains = botLegend ? getCardDomains(botLegend) : ['Calm' as Domain, 'Body' as Domain];

  // Find champion cards
  const playerChampion = playerDeckCards.find((c) => isChampionUnit(c)) || null;
  const botChampion = botDeckCards.find((c) => isChampionUnit(c)) || null;

  // Remove ONE copy of the champion from the deck (it starts in the champion zone)
  const withoutOne = (cards: Card[], champ: Card | null) => {
    if (!champ) return [...cards];
    const i = cards.findIndex((c) => c.id === champ.id);
    return i < 0 ? [...cards] : [...cards.slice(0, i), ...cards.slice(i + 1)];
  };
  const playerMainDeck = shuffle(withoutOne(playerDeckCards, playerChampion));
  const botMainDeck = shuffle(withoutOne(botDeckCards, botChampion));

  // Increase victory score if Aspirant's Climb is a battlefield
  let victoryScore = VICTORY_SCORE;
  if (sanitizeText(playerBattlefield.description).includes('Increase the points')) victoryScore++;
  if (sanitizeText(botBattlefield.description).includes('Increase the points')) victoryScore++;

  const state: GameState = {
    player: {
      side: 'player',
      name: 'Vous',
      score: 0,
      legend: playerLegend,
      championCard: playerChampion,
      championAvailable: !!playerChampion,
      mainDeck: playerMainDeck.slice(INITIAL_HAND_SIZE),
      hand: playerMainDeck.slice(0, INITIAL_HAND_SIZE),
      trash: [],
      runeDeck: createRuneDeck(playerDomains),
      runesInPlay: [],
      units: [],
      gear: [],
      cardsPlayedThisTurn: 0,
    },
    opponent: {
      side: 'opponent',
      name: 'Bot',
      score: 0,
      legend: botLegend,
      championCard: botChampion,
      championAvailable: !!botChampion,
      mainDeck: botMainDeck.slice(INITIAL_HAND_SIZE),
      hand: botMainDeck.slice(0, INITIAL_HAND_SIZE),
      trash: [],
      runeDeck: createRuneDeck(botDomains),
      runesInPlay: [],
      units: [],
      gear: [],
      cardsPlayedThisTurn: 0,
    },
    battlefields: [
      {
        id: 'battlefield1',
        card: playerBattlefield,
        controlledBy: null,
        conqueredTurn: 0,
      },
      {
        id: 'battlefield2',
        card: botBattlefield,
        controlledBy: null,
        conqueredTurn: 0,
      },
    ],
    currentTurn: 'player',
    turnNumber: 1,
    phase: 'main',
    winner: null,
    victoryScore,
    isSecondPlayerFirstTurn: true,
  };

  logs.push({
    turn: 0,
    side: 'system' as Side,
    message: `🎮 Partie lancée! Premier à ${victoryScore} points.`,
    type: 'system',
  });

  if (playerLegend) {
    logs.push({
      turn: 0,
      side: 'player',
      message: `👑 Votre Légende: ${playerLegend.name}`,
      type: 'info',
    });
  }
  if (botLegend) {
    logs.push({
      turn: 0,
      side: 'opponent',
      message: `👑 Légende adverse: ${botLegend.name}`,
      type: 'info',
    });
  }

  logs.push({
    turn: 0,
    side: 'system' as Side,
    message: `🏰 Champs de bataille: ${playerBattlefield.name} & ${botBattlefield.name}`,
    type: 'system',
  });

  // First turn: channel + draw
  channelRunes(state.player, CHANNEL_PER_TURN, logs, 1);
  // No draw on first turn (already drew initial hand)

  return state;
}

// ─── Start of turn sequence ──────────────────────────────
export function startTurn(state: GameState, side: Side, logs: LogEntry[]): void {
  const player = state[side];
  player.cardsPlayedThisTurn = 0;
  state.currentTurn = side;
  state.phase = 'main';

  // Damage is removed at the end of every turn
  for (const s of ['player', 'opponent'] as Side[]) {
    for (const u of state[s].units) u.damageTaken = 0;
  }

  // A. Awaken
  awakenPhase(player);

  // B. Beginning (score held battlefields, kill temporaries)
  beginningPhase(player, state, logs);

  // Check winner after scoring
  const w = checkWinner(state);
  if (w) {
    state.winner = w;
    return;
  }

  // C. Channel runes
  const channelCount =
    side === 'opponent' && state.isSecondPlayerFirstTurn ? CHANNEL_FIRST_TURN_P2 : CHANNEL_PER_TURN;
  if (side === 'opponent') state.isSecondPlayerFirstTurn = false;
  channelRunes(player, channelCount, logs, state.turnNumber);

  // D. Draw
  if (state.turnNumber > 1 || side === 'opponent') {
    drawCards(player, 1, logs, state.turnNumber);
  }

  logs.push({
    turn: state.turnNumber,
    side,
    message: `🎯 Tour ${state.turnNumber} — ${side === 'player' ? 'Votre tour' : 'Tour du Bot'}`,
    type: 'system',
  });
}

// ─── Get all units at a battlefield ──────────────────────
export function getUnitsAtBattlefield(
  state: GameState,
  side: Side,
  bfId: 'battlefield1' | 'battlefield2'
): UnitInstance[] {
  return state[side].units.filter((u) => u.location === bfId);
}

// ─── Get units in base ───────────────────────────────────
export function getUnitsInBase(state: GameState, side: Side): UnitInstance[] {
  return state[side].units.filter((u) => u.location === 'base');
}
